import { gql } from "@apollo/client";

export const GET_ANSWER_DRAFT = gql`
  query (
    $assignmentQuestion: ID!
    $student: ID!
    $tokenUser: ID
    $token: String
  ) {
    answerDraft(
      assignmentQuestion: $assignmentQuestion
      student: $student
      tokenUser: $tokenUser
      token: $token
    ) {
      answer
      answerDocument
      savedAt
      revision
      submittedAnswer
      submittedDocument
    }
  }
`;

export const GET_ASSIGNMENT_QUESTION = gql`
  query ($assignmentQuestion: ID!) {
    assignmentQuestion(assignmentQuestion: $assignmentQuestion) {
      studentInputSettings
      id
      identifier
      order
      nextQuestionId
      previousQuestionId
      essaysMarkedCount
      availableMarks
      stats {
        overallSubmissions {
          target
          current
        }
        averageScore {
          current
          previous
          successfulComparison
        }
      }
      feedback {
        id
      }
      question {
        id
        questionText
        sourceText
        questionType
        isPublished
        tags {
          id
          tag
        }
        markScheme {
          id
          modelAnswerGuidance
          objectives {
            id
            assessmentObjective {
              id
              focusArea
              description
              identifier
            }
            levels {
              minMark
              maxMark
              levelCriteria
            }
          }
        }
      }
      assignment {
        id
        tieredFeatures
        feedbackManuallyReleased
        showFeedbackToStudent
        preferences {
          id
          data
        }
        assignmentTemplate {
          id
        }
        assignmentGroup {
          id
          name
        }
        linkedQuizGroups {
          id
          name
          ongoingJobs {
            id
            status
            progress
            message
            jobType {
              jobType
            }
          }
        }
        name
        createdAt
        dueDate
        answersCount
        tags {
          id
          tag
        }
        status
        schoolClass {
          id
          name
          official
          subject {
            name
          }

          yearGroup {
            id
            yearGroup
            educationLevel {
              id
              level
            }
          }
        }
      }
    }
  }
`;

export const GET_ASSIGNMENT = gql`
  query ($assignment: ID!) {
    assignment(assignment: $assignment) {
      id
      name
      createdAt
      dueDate
      answersCount
      tieredFeatures
      isPublished
      feedbackManuallyReleased
      showFeedbackToStudent
      preferences {
        id
        data
      }
      linkedQuizGroups {
        id
        name
        ongoingJobs {
          id
          status
          progress
          message
          jobType {
            jobType
          }
        }
      }
      ongoingJobs {
        id
        status
        progress
        message
        jobType {
          jobType
        }
      }
      assignmentTemplate {
        id
      }
      assignmentGroup {
        id
        name
      }
      tags {
        id
        tag
      }
      feedback {
        id
      }
      stats {
        overallSubmissions {
          target
          current
        }
        averageScore {
          current
          previous
          successfulComparison
        }
      }
      assignmentQuestions {
        studentInputSettings
        id
        identifier
        availableMarks
        stats {
          overallSubmissions {
            target
            current
          }
          averageScore {
            current
            previous
            successfulComparison
          }
        }
        question {
          id
          isPublished
          questionText
          sourceText
          questionType
          schoolFeedbackFrameworks {
            educationLevel {
              level
            }
          }
          educationLevel {
            id
          }
          subject {
            id
          }
          tags {
            id
            tag
          }
          markScheme {
            id
            modelAnswerGuidance
            name
            tags {
              tag
            }
            objectives {
              id
              assessmentObjective {
                id
                focusArea
                description
                identifier
              }
              levels {
                minMark
                maxMark
                levelCriteria
              }
            }
          }
        }
      }
      schoolClass {
        id
        name
        official
        academicYear {
          id
          name
        }
        yearGroup {
          id
          yearGroup
          educationLevel {
            id
            level
          }
        }
        subject {
          id
          name
        }
      }
      existingStudents {
        id
        pseudonymisedAt
        firstName
        lastName
        identifier
      }
    }
  }
`;
export const GET_ASSIGNMENT_TEMPLATE = gql`
  query ($assignmentTemplate: ID!) {
    assignmentTemplate(assignmentTemplate: $assignmentTemplate) {
      id
      name
      summary
      popularity
      tags {
        id
        tag
      }
      assignmentTemplateQuestions {
        id
        question {
          id
          questionType
          options {
            id
            text
            isCorrect
          }
          questionText
          sourceText
          tags {
            id
            tag
          }
          markScheme {
            id
            modelAnswerGuidance
            objectives {
              id

              assessmentObjective {
                id
                focusArea
                description
                identifier
                subject {
                  name
                }
              }
              levels {
                minMark
                maxMark
                levelCriteria
              }
            }
          }
        }
      }
      educationLevel {
        id
        level
        yearGroups {
          id
          yearGroup
        }
      }
      subjects {
        id
        name
        parent {
          id
          name
        }
      }
    }
  }
`;

export const GET_OVERALL_FEEDBACK = gql`
  query ($assignmentQuestionFeedback: ID, $assignmentFeedback: ID) {
    overallFeedback(
      assignmentQuestionFeedback: $assignmentQuestionFeedback
      assignmentFeedback: $assignmentFeedback
    ) {
      ... on AssignmentFeedbackNode {
        id
        specificAreasOfImprovement
        feedback
        lastUpdated
        allowGenerate
        canonicalEntityInsights {
          id
          label
          explanation
          entityType
          supportingText
          processed
          associatedObjectId
        }
      }
      ... on AssignmentQuestionFeedbackNode {
        id
        specificAreasOfImprovement
        feedback
        lastUpdated
        allowGenerate
        canonicalEntityInsights {
          id
          label
          explanation
          entityType
          supportingText
          processed
          associatedObjectId
        }
      }
    }
  }
`;

export const GET_STUDENT_ASSIGNMENT_ANSWERS = gql`
  query ($assignment: ID!, $student: ID) {
    studentAssignmentAnswers(assignment: $assignment, student: $student) {
      lastAssignmentScore
      assignmentAvailableMarks
      nextStudentId
      previousStudentId
      lastSubmitted
      student {
        id
        pseudonymisedAt
        identifier
      }
      assignment {
        id
        disableSubmission
        showFeedbackToStudent
        preferences {
          id
          data
        }
        assignmentQuestions {
          studentInputSettings
          id
          identifier
          order
          availableMarks
          question {
            questionType
            questionText
            sourceText
            markScheme {
              id
              modelAnswerGuidance
              objectives {
                id
                assessmentObjective {
                  id
                  focusArea
                  description
                  identifier
                }
                levels {
                  id
                  minMark
                  maxMark
                  levelCriteria
                }
              }
            }
          }
        }
      }
      answers {
        id
        availableMarks
        answer
        answerDocument
        status
        submissionAllowance {
          attempts
          limit
          remaining
          allowed
          unlimited
        }
        submittedAt
        viewed
        assignmentQuestion {
          studentInputSettings
          id
        }
        handwrittenAnswerFile {
          id
          preSignedUrl
          extractionQualityReview {
            reviewSuggested
            qualityLevel
            qualityReason
          }
        }
        student {
          id
          pseudonymisedAt
          identifier
        }
        canonicalEntityInsights {
          id
          label
          explanation
          entityType
          supportingText
          processed
          associatedObjectId
        }
        teacherFeedback {
          id
          mark
          specificAreasOfImprovement
          feedback
          approvedAt
          spellingAndGrammar {
            id
            reasoning
            mistake {
              incorrectSpelling
              correctSpelling
            }
            locations {
              startIndex
              endIndex
            }
          }
          assessmentObjectiveFeedback {
            id
            mark
            availableMarks
            assessmentObjectiveEvidence {
              id
              evidence
              reasoning
              feedback
              locations {
                startIndex
                endIndex
              }
            }
            assessmentObjective {
              id
              identifier
              focusArea
            }
          }
        }
      }
    }
  }
`;
export const GET_ANONYMOUS_ANSWER = gql`
  query ($answer: ID!) {
    anonymousAnswer(answer: $answer) {
      id
      availableMarks
      answer
      answerDocument
      teacherFeedback {
        id
        mark
      }
    }
  }
`;

export const GET_ALL_ASSIGNMENTS = gql`
  query (
    $selectedStatus: String
    $schoolClass: ID
    $searchString: String
    $isPublished: Boolean
    $showAllClasses: Boolean
    $first: Int
    $after: String
    $academicYear: ID
  ) {
    allAssignments(
      schoolClass: $schoolClass
      isPublished: $isPublished
      searchString: $searchString
      selectedStatus: $selectedStatus
      showAllClasses: $showAllClasses
      first: $first
      after: $after
      academicYear: $academicYear
    ) {
      edges {
        cursor
        node {
          id
          name
          canAccessOnTier
          createdAt
          dueDate
          status
          isPublished
          essaysMarkedCount
          tags {
            id
            tag
          }
          schoolClass {
            id
            name
            academicYear {
              id
              name
            }
            yearGroup {
              id
              yearGroup
              educationLevel {
                id
                level
              }
            }
            subject {
              id
              name
            }
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_ALL_ASSIGNMENT_TEMPLATES = gql`
  query ($schoolClass: ID) {
    allAssignmentTemplates(schoolClass: $schoolClass) {
      id
      name
      summary
      popularity
      educationLevel {
        level
      }
      subjects {
        name
        parent {
          id
          name
        }
      }
      tags {
        tag
      }
    }
  }
`;

export const GET_ANSWERS_AND_IDENTIFIERS = gql`
  query ($assignmentQuestion: ID!) {
    answers(assignmentQuestion: $assignmentQuestion) {
      id
      assignment {
        id
      }
      answer
      answerDocument
      viewed
      submittedAt
      status
      student {
        id
        pseudonymisedAt
        identifier
      }
      percentage
      handwrittenAnswerFile {
        id
        preSignedUrl
        extractionQualityReview {
          reviewSuggested
          qualityLevel
          qualityReason
        }
      }
    }
    existingStudents(assignmentQuestion: $assignmentQuestion) {
      id
      pseudonymisedAt
      identifier
    }
  }
`;

export const GET_ASSIGNMENT_UPLOAD_JOB_COUNTS = gql`
  query ($assignment: ID, $assignmentGroup: ID) {
    assignmentUploadJobCounts(
      assignment: $assignment
      assignmentGroup: $assignmentGroup
    ) {
      pending
      processing
      needsReview
      failed
      completed
    }
  }
`;

export const GET_ASSIGNMENT_UPLOAD_JOBS = gql`
  query (
    $assignment: ID
    $assignmentGroup: ID
    $bucket: AssignmentUploadJobBucket!
    $searchString: String
    $first: Int!
    $after: String
  ) {
    assignmentUploadJobs(
      assignment: $assignment
      assignmentGroup: $assignmentGroup
      bucket: $bucket
      searchString: $searchString
      first: $first
      after: $after
    ) {
      edges {
        node {
          id

          autoMappedCount
          manualMappedCount
          totalItemsCount
          unmappedCount

          unansweredQuestions {
            id
            pages
            isConfirmed
            assignmentQuestion {
              studentInputSettings
              id
            }
          }

          mappedItems {
            edges {
              node {
                id
                autoMapped
                confidenceScore
                pages

                assignmentQuestion {
                  studentInputSettings
                  id
                  order
                  identifier

                  question {
                    id
                  }
                }

                answer {
                  id

                  handwrittenAnswerFile {
                    id
                    originalFilename
                    preSignedUrl
                  }
                }
              }
            }
          }

          assignment {
            id

            assignmentQuestions {
              studentInputSettings
              id
              order
              identifier

              question {
                id
                questionText
                sourceText
                questionType
              }
            }
          }

          student {
            id
            identifier
            pseudonymisedAt
            firstName
            lastName
          }

          job {
            id
            progress
            createdAt
            status

            logs {
              message
              hideFromUser
            }
          }
        }
      }

      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_ASSIGNMENT_UPLOAD_MAPPING_REVIEW = gql`
  query ($fileProcessingResult: ID!) {
    assignmentUploadMappingReview(fileProcessingResult: $fileProcessingResult) {
      sourcePageCount
      sourceFileUrl
    }
  }
`;

export const GET_ALL_ASSIGNMENT_GROUPS = gql`
  query ($first: Int, $after: String, $showAllClasses: Boolean) {
    allAssignmentGroups(
      first: $first
      after: $after
      showAllClasses: $showAllClasses
    ) {
      edges {
        cursor
        node {
          id
          name
          educationLevel {
            id
            level
          }
          subject {
            id
            name
          }
          assignments {
            id
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_ASSIGNMENT_GROUP = gql`
  query ($assignmentGroup: ID!) {
    assignmentGroup(assignmentGroup: $assignmentGroup) {
      id
      name
      educationLevel {
        id
        level
      }
      subject {
        id
        name
      }
      assignments {
        id
        name
        dueDate
        status
        isPublished
        answersCount
        percentageComplete
        schoolClass {
          id
          name
          yearGroup {
            id
            yearGroup
          }
          school {
            id
            name
          }
        }
        tags {
          id
          tag
        }
      }
    }
  }
`;

export const GET_CLASSES_FOR_ASSIGNMENT_GROUP = gql`
  query ($assignmentGroup: ID!) {
    classesForAssignmentGroup(assignmentGroup: $assignmentGroup) {
      schoolClass {
        id
        name
        school {
          name
        }
        subject {
          id
          name
        }
        yearGroup {
          id
          yearGroup
        }
      }
      assignment {
        id
        name
      }
    }
  }
`;

export const GET_BULK_FILE_UPLOADS = gql`
  query (
    $assignment: ID
    $assignmentGroup: ID
    $bucket: BulkFileUploadBucket
    $searchString: String
    $first: Int
    $after: String
  ) {
    bulkFileUploads(
      assignment: $assignment
      assignmentGroup: $assignmentGroup
      bucket: $bucket
      searchString: $searchString
      first: $first
      after: $after
    ) {
      edges {
        node {
          id
          originalFilename
          processingStatus
          uploadedAt
          processingStartedAt
          processedAt

          assignment {
            id
            name
          }

          assignmentGroup {
            id
            name
          }

          uploadedBy {
            id
            email
          }

          job {
            id
            progress
            createdAt
            status

            logs {
              message
              hideFromUser
            }
          }
        }
      }

      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_BULK_FILE_UPLOAD_COUNTS = gql`
  query ($assignment: ID, $assignmentGroup: ID) {
    bulkFileUploadCounts(
      assignment: $assignment
      assignmentGroup: $assignmentGroup
    ) {
      pending
      inProgress
      failed
      completed
    }
  }
`;
